import type { InvoiceDetailDto, PackingListDetailDto, PiDetailDto } from "@hisense/shared";
import { totalMinor } from "@hisense/shared";
import { documentTotals } from "./documentTotals";
import { exportCsv, fileStamp, type CsvColumn } from "@/lib/exportCsv";
import { piDocumentFacts } from "./piDocument";
import { majorUnits } from "@/domain/money";

/**
 * Xuất MỘT chứng từ ra file bảng tính.
 *
 * Khác bản in: bản in để gửi khách, file này để kế toán đối chiếu và tính toán
 * — nên tiền là SỐ TRẦN (không ký hiệu, không phân cách nghìn), Excel cộng được
 * ngay. Xem majorUnits.
 *
 * Đơn giá 4 chữ số thập phân, tổng 2 — đúng thang tiền của dự án.
 *
 * LOẠI TIỀN nằm ở một CỘT RIÊNG, không ghép vào ô tiền: ghép vào thì ô thành
 * chuỗi và `=SUM()` trả về 0 — mất đúng cái lý do người ta xuất ra Excel. Nhưng
 * bỏ hẳn cũng sai: hệ thống có chứng từ USD lẫn HKD, một file toàn số trần
 * không nói được nó là tiền gì, và hai file dán chung một sổ là cộng nhầm.
 */

/** Dòng tổng ở cuối bảng — nhãn ở cột đầu, số ở cột thành tiền. */
type TotalRow = { totalLabel: string; totalValue: number | null };
const isTotal = (r: unknown): r is TotalRow =>
  typeof r === "object" && r !== null && "totalLabel" in r;

type MoneyLine = {
  productCode: string | null; subRef: string | null; description: string | null;
  /** Giá EXW của dòng, nếu chứng từ chào theo EXW. Xem cột Đơn giá bên dưới. */
  exwPrice?: number | null;
  /** Bốn cột hệ cũ LUÔN xuất cho PI; hoá đơn không có nên để tuỳ chọn. */
  customerStyleNo?: string | null;
  moq?: number | null;
  dtdPrice?: number | null;
  underMoqPrice?: number | null;
  color: string | null; size: string | null; quantity: number; unit: string | null;
  unitPrice: number; amount: number; lotNumber: string | null;
};

const lineCols = (
  t: (k: string, o?: Record<string, unknown>) => string,
  currency: string,
  piMode = false,
): CsvColumn<MoneyLine | TotalRow>[] => [
  // Nhãn dòng tổng đi ở cột ĐẦU: đặt nó vào một cột số (đơn giá) thì Excel đọc
  // cả cột thành chữ và mọi công thức trên cột đó hỏng theo.
  { header: t("print.code"), value: (r) => (isTotal(r) ? r.totalLabel : r.productCode) },
  /**
   * MÃ PHỤ phải có trong tệp xuất.
   *
   * Trên dữ liệu thật, 667 sản phẩm thuộc 155 nhóm DÙNG CHUNG mã sản phẩm và
   * chỉ khác nhau ở mã phụ. Bỏ cột này thì dây rút 140cm, 145cm và 150cm ra ba
   * dòng giống hệt nhau trong Excel — kế toán không phân biệt được dòng nào là
   * dòng nào, mà bản in thì có cột này (printInvoice/printPi đều in Sub Ref).
   */
  { header: t("common.subRef"), value: (r) => (isTotal(r) ? null : r.subRef) },
  // Mã kiểu của KHÁCH: mã họ dùng để đối chiếu ở đầu bên kia. Hệ cũ luôn xuất.
  { header: t("common.customerStyleNo"), value: (r) => (isTotal(r) ? null : r.customerStyleNo ?? null) },
  { header: t("print.description"), value: (r) => (isTotal(r) ? null : r.description) },
  { header: t("common.color"), value: (r) => (isTotal(r) ? null : r.color) },
  { header: t("common.size"), value: (r) => (isTotal(r) ? null : r.size) },
  { header: t("lot.number"), value: (r) => (isTotal(r) ? null : r.lotNumber) },
  { header: t("print.qty"), value: (r) => (isTotal(r) ? null : r.quantity) },
  { header: t("products.unit"), value: (r) => (isTotal(r) ? null : r.unit) },
  {
    // PI bills unitPrice; EXW is a separate reference column.
    header: t("print.unitPrice"),
    value: (r) =>
      isTotal(r) ? null : majorUnits(!piMode && r.exwPrice && r.exwPrice > 0 ? r.exwPrice : r.unitPrice, 4),
  },
  ...(piMode ? [{ header: t("common.exwPrice"), value: (r: MoneyLine | TotalRow) => isTotal(r) || r.exwPrice == null ? null : majorUnits(r.exwPrice, 4) }] : []),
  { header: t("common.moq"), value: (r) => (isTotal(r) ? null : r.moq ?? null) },
  // Ba mức giá chào của hệ cũ. Bản in PI có cột DTD Price và Under MOQ khi
  // chứng từ dùng tới, nhưng tệp xuất thì chưa bao giờ có — kế toán đối chiếu
  // tệp với tờ giấy đã gửi khách sẽ thiếu đúng những con số đã cam kết.
  {
    header: t("common.dtdPrice"),
    value: (r) => (isTotal(r) || !r.dtdPrice ? null : majorUnits(r.dtdPrice, 4)),
  },
  {
    header: t("common.underMoqPrice"),
    value: (r) => (isTotal(r) || !r.underMoqPrice ? null : majorUnits(r.underMoqPrice, 4)),
  },
  {
    header: t("print.amount"),
    value: (r) =>
      isTotal(r) ? (r.totalValue == null ? null : majorUnits(r.totalValue, piMode && r.totalLabel !== t("print.grandTotal") ? 4 : 2)) : majorUnits(r.amount, piMode ? 4 : 2),
  },
  { header: t("common.currency"), value: () => currency },
];

/**
 * Khối tổng của chứng từ, ĐÚNG cách bản in tính.
 *
 * Không có khối này thì file xuất ra chỉ là các dòng hàng: kế toán tự cộng lại
 * sẽ ra một số KHÁC số trên chứng từ đã gửi khách, vì chiết khấu là phần trăm
 * và phụ phí thì không nằm ở dòng nào cả — không có cách nào suy ra từ bảng.
 */
function totalRows(
  doc: { items: readonly { amount: number }[]; upcharge: number; discount: number },
  t: (k: string, o?: Record<string, unknown>) => string,
): TotalRow[] {
  // Phép tính nằm ở MỘT chỗ duy nhất (`documentTotals`), dùng chung với bản in
  // và Excel: trước 15/09/2026 chỗ này làm tròn hai lần nên tệp CSV tự mâu
  // thuẫn — cộng cột không ra chính dòng tổng cuối của nó.
  const { lineSubtotal, base, grand } = documentTotals(doc);
  const rows: TotalRow[] = [
    // Dòng trống ngăn khối tổng khỏi vùng dữ liệu, để lọc/sắp xếp không cuốn
    // các dòng tổng vào giữa bảng.
    { totalLabel: "", totalValue: null },
    { totalLabel: t("print.subtotal"), totalValue: totalMinor(lineSubtotal) },
  ];
  if (doc.upcharge) rows.push({ totalLabel: t("print.upcharge"), totalValue: doc.upcharge });
  if (doc.discount) {
    rows.push({
      totalLabel: t("print.less", { pct: (doc.discount / 100).toFixed(2) }),
      // Số ÂM chứ không phải số dương có nhãn "trừ": cộng cả cột phải ra đúng
      // tổng cuối, nếu không thì bảng tự mâu thuẫn với chính nó.
      totalValue: -totalMinor((base * doc.discount) / 10_000),
    });
  }
  rows.push({ totalLabel: t("print.grandTotal"), totalValue: grand });
  return rows;
}

export function exportInvoiceCsv(
  inv: InvoiceDetailDto,
  t: (k: string, o?: Record<string, unknown>) => string,
): void {
  exportCsv(`invoice-${inv.invNo}-${fileStamp()}`, lineCols(t, inv.currency), [
    ...inv.items,
    ...totalRows(inv, t),
  ]);
}

export function exportPiCsv(
  pi: PiDetailDto,
  t: (k: string, o?: Record<string, unknown>) => string,
): void {
  exportCsv(`proforma-invoice-${pi.piNo}-${fileStamp()}`, lineCols(t, pi.currency, true), [
    ...pi.items,
    ...(() => {
      const f = piDocumentFacts(pi);
      return [{totalLabel: "", totalValue: null},
        {totalLabel:t("print.subtotal"),totalValue:f.lineSubtotal},
        {totalLabel:t("print.upcharge"),totalValue:pi.upcharge ?? 0},
        {totalLabel:t("invoiceWorkspace.roundedBase"),totalValue:f.base},
        {totalLabel:t("print.less",{pct:f.discountPct.toFixed(2)}),totalValue:-f.less},
        {totalLabel:t("print.grandTotal"),totalValue:f.grand}];
    })(),
  ]);
}

/** Dòng của tệp phiếu đóng gói: dòng hàng thật, hoặc một dòng tổng ở cuối. */
type _PlExportRow = PackingListDetailDto["items"][number] | TotalRow;

export function exportPackingListCsv(pl:PackingListDetailDto,t:(key:string,options?:Record<string,unknown>)=>string):void {
 type Row=Partial<PackingListDetailDto["items"][number]>&{label?:string};
 const seen=new Set<string>();const quantities=new Map<string,number>();
 const rows:Row[]=pl.items.map(it=>{const key=it.ctnNo??"";const first=!seen.has(key);seen.add(key);const unit=it.unit||"—";quantities.set(unit,(quantities.get(unit)??0)+it.quantity);return{...it,nwKgs:first?it.nwKgs:null,gwKgs:first?it.gwKgs:null};});
 rows.push({label:""});for(const [unit,quantity] of quantities)rows.push({label:t("print.total"),quantity,unit});
 const weighed=pl.items.every(it=>it.nwKgs!==null&&it.gwKgs!==null);rows.push({label:`${t("common.cartons")}: ${pl.totalCtns}`,nwKgs:weighed?pl.totalNw:null,gwKgs:weighed?pl.totalGw:null});
 exportCsv(`packing-list-${pl.plNo}-${fileStamp()}`,[{header:t("packingLists.ctnNo"),value:(r:Row)=>r.label??r.ctnNo},{header:t("print.code"),value:(r:Row)=>r.itemNo},{header:t("common.subRef"),value:(r:Row)=>r.subRef},{header:t("packingLists.customerPo"),value:(r:Row)=>r.customerPo},{header:t("print.description"),value:(r:Row)=>r.description},{header:t("common.color"),value:(r:Row)=>r.color},{header:t("common.size"),value:(r:Row)=>r.size},{header:t("lot.number"),value:(r:Row)=>r.lotNumber},{header:t("print.qty"),value:(r:Row)=>r.quantity},{header:t("products.unit"),value:(r:Row)=>r.unit},{header:t("packingLists.netWeight"),value:(r:Row)=>r.nwKgs},{header:t("packingLists.grossWeight"),value:(r:Row)=>r.gwKgs},{header:t("packingLists.measurement"),value:(r:Row)=>r.measurementCm}],rows);
}
